/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import MainNavigation from "@/components/layout/main-navigation";
import { productFeatureCards } from "@/content/landing";
import { AI_TOOL_COUNT, CTA, TRUST } from "@/content/landing/constants";
import {
  CheckCircle,
  Trophy,
  Hammer,
  BarChart3,
  Briefcase,
  Calculator,
  CalendarDays,
  DollarSign,
  FileText,
  FolderOpen,
  Layers,
  Mail,
  Sparkles,
  Users,
  ArrowRight,
  FileSignature,
  ShoppingCart,
  Handshake,
  CalendarClock,
  Sunrise,
  Globe,
  Building2,
  Ruler,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import { FadeIn } from "@/components/landing/primitives/fade-in";

/** Group the 8 feature cards under 3 pillars */
const pillars: {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Indices into productFeatureCards */
  cardIndices: number[];
}[] = [
  {
    icon: Trophy,
    title: "Win Work",
    description: "Everything you need to go from takeoff to signed contract.",
    cardIndices: [0, 2], // Estimates & proposals, Takeoffs & data import
  },
  {
    icon: Hammer,
    title: "Run Jobs",
    description: "Track projects, budgets, invoices, and schedules.",
    cardIndices: [3, 5, 6], // Projects, Clients/contacts/suppliers, Budgets & invoices
  },
  {
    icon: BarChart3,
    title: "Know Your Numbers",
    description: "Cost databases, AI assistant, and org-wide visibility.",
    cardIndices: [4, 1, 7], // Costs, Documents & AI, Org/security/collab
  },
];

export function FeaturesPageClient() {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Hero */}
      <MarketingPageHero
        variant="dark"
        badge="Features"
        title={
          <>
            One platform.{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              Every workflow.
            </span>
          </>
        }
        description="Estimates, proposals, projects, budgets, invoices, cost databases, and an AI assistant — all connected, all in one place."
      />

      {/* Feature Grid grouped by pillars */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background"
        aria-labelledby="main-features"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          {pillars.map((pillar, pillarIndex) => {
            const PillarIcon = pillar.icon;
            const cards = pillar.cardIndices
              .map((i) => productFeatureCards[i])
              .filter((c): c is NonNullable<typeof c> => c !== undefined);
            return (
              <div
                key={pillar.title}
                className={pillarIndex > 0 ? "mt-16 md:mt-24" : ""}
              >
                {/* Pillar header */}
                <header className="mb-8 md:mb-10 max-w-3xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-secondary/10">
                      <PillarIcon className="w-5 h-5 text-secondary" />
                    </div>
                    <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground font-display">
                      {pillar.title}
                    </h2>
                  </div>
                  <p className="text-[clamp(14px,1.5vw,16px)] leading-[1.5] text-muted-foreground">
                    {pillar.description}
                  </p>
                </header>

                {/* Cards in this pillar */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cards.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <FadeIn
                        key={feature.title}
                        as="article"
                        className="card-premium group rounded-2xl p-6 border border-border bg-muted/30"
                        delay={(pillarIndex * 3 + index) * 100}
                      >
                        <div
                          className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary transition-transform duration-300 group-hover:scale-110"
                          aria-hidden="true"
                        >
                          <Icon className="w-6 h-6 text-white" />
                        </div>

                        <h3 className="text-lg font-bold text-foreground mb-3 font-display">
                          {feature.title}
                        </h3>

                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                          {feature.description}
                        </p>

                        <ul className="space-y-2" role="list">
                          {feature.includes.slice(0, 4).map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-2 text-sm"
                            >
                              <CheckCircle
                                className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5"
                                aria-hidden="true"
                              />
                              <span className="text-muted-foreground">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </FadeIn>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* All Feature Pages */}
      <section className="py-16 md:py-24 bg-background-warm">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="mb-8 md:mb-10 max-w-3xl">
            <span className="section-badge mb-4">Explore Features</span>
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
              Learn more about each feature
            </h2>
          </header>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              // Win Work
              {
                icon: Calculator,
                title: "Estimates & Takeoffs",
                description:
                  "Build scopes, price work confidently, and send cleaner estimates without spreadsheet chaos.",
                href: "/features/estimates",
              },
              {
                icon: Ruler,
                title: "Blueprint Takeoffs",
                description:
                  "Measure plans faster across multi-page sets and move quantity data directly into estimating.",
                href: "/features/takeoffs",
              },
              {
                icon: FileText,
                title: "Proposals & Client Portal",
                description:
                  "Send professional proposals, track client engagement, and collect approvals with less friction.",
                href: "/features/proposals-portal",
              },
              {
                icon: FileSignature,
                title: "Change Orders",
                description:
                  "Manage scope changes with clear approval history and cleaner budget impact tracking.",
                href: "/features/change-orders",
              },
              {
                icon: Users,
                title: "CRM",
                description:
                  "Keep leads, clients, jobsites, and follow-up history in one place so deals do not get lost.",
                href: "/features/crm",
              },
              // Run Jobs
              {
                icon: Briefcase,
                title: "Project Management",
                description:
                  "Run jobs from kickoff to close-out with task, schedule, and cost visibility your team can act on.",
                href: "/features/project-management",
              },
              {
                icon: CalendarClock,
                title: "Schedule & Dispatch",
                description:
                  "Coordinate dispatch, field assignments, and recurring service schedules from one operational view.",
                href: "/features/schedule",
              },
              {
                icon: Sunrise,
                title: "Field Ops & Time Tracking",
                description:
                  "Give field teams a simple mobile web workflow for clock-in, assignment context, and accountable time entry.",
                href: "/features/field-ops",
              },
              {
                icon: ShoppingCart,
                title: "Purchase Orders",
                description:
                  "Control ordering from draft through receiving and use purchase outcomes to improve future pricing.",
                href: "/features/purchase-orders",
              },
              {
                icon: Handshake,
                title: "Suppliers & Subcontractors",
                description:
                  "Manage trade partners with clearer quote context, performance visibility, and stronger purchasing decisions.",
                href: "/features/subcontractors",
              },
              {
                icon: CalendarDays,
                title: "Calendar",
                description:
                  "Keep deadlines and follow-ups visible with auto-created events and calendar app subscription support.",
                href: "/features/calendar",
              },
              // Know Your Numbers
              {
                icon: DollarSign,
                title: "Budgets & Invoices",
                description:
                  "Protect margin from planning to payment with billing workflows that match real contract types.",
                href: "/features/budgets-invoices",
              },
              {
                icon: BarChart3,
                title: "Reporting & Analytics",
                description:
                  "See where you are making or losing money with practical reporting for leadership and PMs.",
                href: "/features/reporting",
              },
              {
                icon: Layers,
                title: "Cost Database",
                description:
                  "Use a shared cost foundation so every estimate starts from real numbers instead of guesswork.",
                href: "/features/cost-database",
              },
              {
                icon: Building2,
                title: "Divisions",
                description:
                  "Run multiple divisions with role-aware visibility and a clean company-wide rollup for leadership.",
                href: "/features/divisions",
              },
              {
                icon: Sparkles,
                title: "AI Assistant (Hank)",
                description: `${AI_TOOL_COUNT} tools. Plain-English queries of your real data. Cost matching, estimate audits, scope gap detection.`,
                href: "/features/ai-assistant",
              },
              // Support & Collaboration
              {
                icon: Globe,
                title: "Client Portal",
                description:
                  "Give clients a cleaner experience for proposal review, response, and shared document access.",
                href: "/features/client-portal",
              },
              {
                icon: FolderOpen,
                title: "Documents",
                description:
                  "Keep project files organized with version history, connected context, and review-first receipt matching.",
                href: "/features/documents",
              },
              {
                icon: Users,
                title: "Team & Permissions",
                description:
                  "Assign roles that match real contractor jobs so teams collaborate safely without access confusion.",
                href: "/features/collaboration",
              },
              {
                icon: Mail,
                title: "Communications",
                description:
                  "Use launch-ready customer communication tools and notification workflows, with inbox sync planned post-launch.",
                href: "/features/email-hub",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="group rounded-2xl border border-border bg-background p-6 hover:border-secondary/30 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-secondary" />
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/0 group-hover:text-secondary transition-colors ml-auto" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-secondary transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <MarketingCTASection
        title="See it for yourself"
        description="7-day free trial. No credit card required. Pick your trade and start estimating."
        primaryCTA={{ text: CTA.PRIMARY, href: CTA.PRIMARY_HREF }}
        secondaryCTA={{ text: CTA.SECONDARY_CONTACT, href: CTA.CONTACT_HREF }}
        trustIndicators={[TRUST.INLINE]}
      />

      <Footer />
    </div>
  );
}
